import { ref } from "vue"
import { dateLocaleOf, LANGUAGE_NAMES, LOCALES, normalizeLocale, translate, type Locale } from "./translate"

/**
 * The language Hub's screens speak, as a reactive setting: the app takes the phone's language,
 * the POS its own (setLocale). The texts themselves are lib/translate.ts's.
 */
export type { Locale }
export { LANGUAGE_NAMES }

const locale = ref<Locale>("en")
const listeners: ((locale: Locale) => void)[] = []

/** "fi_FI", "fi-FI" or "fi"; anything Hub doesn't speak is English. */
export function setLocale(value: string | null | undefined) {
  const next = normalizeLocale(value)
  if (next === locale.value) return
  locale.value = next
  for (const listener of listeners) listener(next)
}

/** Told when the language changes, e.g. to tell Hub's server for emails and pushes. */
export function onLocaleChange(listener: (locale: Locale) => void) {
  listeners.push(listener)
}

/** The phone's or browser's first language that Hub speaks, else English. */
export function deviceLocale(): Locale {
  const preferred = typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language])
  return normalizeLocale(preferred.find((l) => normalizeLocale(l) !== "en" || /^en\b/i.test(l)) ?? "en")
}

/** The app's language: the one chosen in the app, else the phone's. The app's page says so too. */
export function applyLanguage(chosen: Locale | null) {
  setLocale(chosen ?? deviceLocale())
  if (typeof document !== "undefined") document.documentElement.lang = locale.value
}

/** The phone's language. */
export function setLocaleFromDevice() {
  applyLanguage(null)
}

export function currentLocale(): Locale {
  return locale.value
}

export function supportedLocales(): readonly Locale[] {
  return LOCALES
}

/** For dates and times in the current language. */
export function dateLocale(): string {
  return dateLocaleOf(locale.value)
}

/** A text in the current language; {name} in it is replaced from `vars`. */
export function t(text: string, vars?: Record<string, string | number>): string {
  return translate(locale.value, text, vars)
}

/** One or many: tn(n, "{n} message", "{n} messages"). */
export function tn(n: number, one: string, many: string, vars: Record<string, string | number> = {}): string {
  return t(n === 1 ? one : many, { n, ...vars })
}
